import React, { useEffect } from "react";
import Chart from "react-apexcharts";
import { Select, DatePicker } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { Tabs, Tab, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  FaMoneyBillAlt,
  FaChartLine,
  FaHistory,
  FaShoppingCart,
  FaCalculator,
  FaBan,
} from "react-icons/fa";
import { FiToggleLeft, FiToggleRight } from "react-icons/fi";
import { MdRefresh } from "react-icons/md";
import dayjs from "dayjs";
import "./Analytics.css";
import CopyIcon from "../../components/CopyIcon/CopyIcon";

// AmCharts Imports
import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_india2019High from "@amcharts/amcharts4-geodata/india2019High";
import animated from "@amcharts/amcharts4/themes/animated";

// Assets & Custom Hooks
// import myImage from "../../assets/images/circle.svg"; // Ensure this file exists
import { useAnalytics } from "../../customHooks/Analytics/useAnalytics";
import { useSidebarNavigation } from "../../customHooks/SidebarNavigation/useSidebarNavigation";
import IgmAnalyticsDashboard from "../IgmDashboard/IgmAnalyticsDashboard";
import RsfAnalyticsDashboard from "../RsfDashboard/RsfAnalyticsDashboard";

am4core.useTheme(animated);
const { Option } = Select;

const Analytics = () => {
  const [mapLegends, setMapLegends] = React.useState([]);
  const [bestSellerMetric, setBestSellerMetric] = React.useState("revenue");
  const navigate = useNavigate();
  const {
    data: {
      lifetimeSales,
      tileMetrics,
      tableMetrics,
      trendMetrics,
      mapMetrics,
      dynamicMetrics,
    },
    // startDate,
    // endDate,
    key,
    setKey,
    viewBy,
    setViewBy,
    handleSelectChange,
    onDateRangeChange,
    dateRange,
    selectedDateCategory,
    selectedCategory,
    setSelectedCategory,
    chartType,
    setChartType,
    topCategoryCount,
    setTopCategoryCount,
    handleRefresh,
    // openSelectStore,
    // setOpenSelectStore,
    // handleChangeStore,
    // handleStoreSearch,
    // search,
    // stores,
  } = useAnalytics();

  // Get user services to determine which analytics tabs to show
  const { userServices, hasService } = useSidebarNavigation();

  // Set default tab based on user's services
  // This runs whenever services change (including store changes)
  useEffect(() => {
    // Determine which tab should be active by default based on user's services
    if (hasService(1) || hasService(2)) {
      // ISN/MSN service - show Orders tab
      setKey("orders");
    } else if (hasService(6)) {
      // IGM service - show IGM tab
      setKey("igm");
    } else if (hasService(5)) {
      // RSF service - show RSF tab
      setKey("rsf");
    }
  }, [userServices, setKey, hasService]);

  // Validate activeKey when services change
  // If current activeKey is not available, switch to a valid tab
  useEffect(() => {
    const validTabs = [];
    if (hasService(1) || hasService(2)) validTabs.push("orders");
    if (hasService(6)) validTabs.push("igm");
    if (hasService(5)) validTabs.push("rsf");

    // If current key is not in valid tabs, switch to the first valid tab
    if (validTabs.length > 0 && !validTabs.includes(key)) {
      setKey(validTabs[0]);
    }
  }, [key, hasService, setKey]);

  const shortName = (p) => {
    const name = p?.productname;
    if (!name) return "Test Product";
    const parts = name.split("/");
    const shortNameText = parts[0] || name;
    const maxLength = 30;
    return shortNameText.length > maxLength
      ? shortNameText.substring(0, maxLength) + "..."
      : shortNameText;
  };

  const formatWholeNumber = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "N/A";
    return Math.trunc(numericValue).toLocaleString("en-IN");
  };

  // Mock Permissions - Replace with actual hooks if available
  const canViewOrderAnalytics = true; // usePermission("ORDERS_ANALYTICS_VIEW");
  const canViewIgmAnalytics = true; // usePermission("IGM_ANALYTICS_VIEW");

  // Map Initialization
  useEffect(() => {
    let chart = am4core.create("chartdiv", am4maps.MapChart);
    chart.geodata = am4geodata_india2019High;
    chart.projection = new am4maps.projections.Miller();

    // Remove watermark
    if (chart.logo) {
      chart.logo.disabled = true;
    }

    const polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.useGeodata = true;

    const stateOrderData = mapMetrics.map((state) => ({
      id: state.stateid,
      name: state.statename,
      value: parseFloat(state.ordervalue),
      count: parseInt(state.ordercount, 10),
    }));

    polygonSeries.data = stateOrderData;

    const polygonTemplate = polygonSeries.mapPolygons.template;
    polygonTemplate.tooltipText = `{name}
    Order Value: ₹{value.formatNumber('#,###')}
    Order Count: {count}`;
    polygonTemplate.nonScalingStroke = true;
    polygonTemplate.strokeWidth = 0.5;

    // Add click event to navigate to Orders page with state filter
    polygonTemplate.events.on("hit", (event) => {
      const stateData = event.target.dataItem.dataContext;
      if (stateData && stateData.name) {
        navigate("/orders", {
          state: { selectedState: stateData.name },
        });
      }
    });

    // Add cursor pointer on hover
    polygonTemplate.cursorOverStyle = [{ property: "cursor", value: "pointer" }];

    polygonSeries.heatRules.push({
      property: "fill",
      target: polygonTemplate,
      min: am4core.color("#f3f3f3"),
      //max: am4core.color("#ff9900"),
      dataField: "value",
      logarithmic: true,
    });

    // Color palette for top 5 states
    const colorPalette = [
      "#66BB6A", // Green
      "#A3D9A5", // Light Green
      "#FFEB3B", // Yellow
      "#A2CFF7", // Blue
      "#E57373", // Red
    ];

    // Legend Logic
    const top5States = stateOrderData
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)
      .map((item) => item.id);

    polygonSeries.events.on("validated", () => {
      // Create legend data
      const legendData = [];

      // Apply colors to top 5 states
      top5States.forEach((stateId, index) => {
        const stateData = stateOrderData.find((state) => state.id === stateId);
        const polygon = polygonSeries.mapPolygons.values.find(
          (p) => p.dataItem.dataContext.id === stateId,
        );

        if (polygon && stateData) {
          // Apply color based on rank in top 5
          polygon.fill = am4core.color(colorPalette[index]);
          const hexColor = colorPalette[index];

          legendData.push({
            id: stateId,
            name: stateData.name,
            value: stateData.value,
            count: stateData.count,
            color: hexColor,
          });
        }
      });

      // Color remaining states with light gray and apply hover effect
      stateOrderData.forEach((stateObj) => {
        if (!top5States.includes(stateObj.id)) {
          const polygon = polygonSeries.mapPolygons.values.find(
            (p) => p.dataItem.dataContext.id === stateObj.id,
          );
          if (polygon) {
            polygon.fill = am4core.color("#f3f3f3");
            // Apply hover effect only to non-top-5 states
            const hs = polygon.states.create("hover");
            hs.properties.fill = am4core.color("#ff7d01");
          }
        }
      });

      setMapLegends(legendData);
    });

    return () => {
      if (chart) {
        chart.dispose();
      }
    };
  }, [mapMetrics, viewBy]);

  // Chart Data Preparation
  const timeSeriesChartData = {
    categories: trendMetrics?.period || [],
    series: [
      {
        name: viewBy === "value" ? "Order Value" : "Order Count",
        data:
          viewBy === "value"
            ? (trendMetrics?.ordervalue || []).map(val => Math.round(val))
            : trendMetrics?.ordercount || [],
      },
    ],
  };

  const getChartData = () => {
    let labels = [];
    let series = [];

    const categoryKey = `${selectedCategory}Metrics`;
    const metrics = dynamicMetrics?.[categoryKey] || [];

    if (!metrics.length) {
      return { labels, series };
    }

    metrics.forEach((period) => {
      period.metrics.forEach((metric) => {
        let label;
        switch (selectedCategory) {
          case "status":
            label = metric.status;
            break;
          case "state":
            label = metric.state || "Unknown";
            break;
          case "buyerApp":
            label = metric.buyerApp;
            break;
          default:
            label = "";
        }

        if (!labels.includes(label)) {
          labels.push(label);
          series.push({ name: label, data: [] });
        }

        const index = labels.indexOf(label);
        series[index].data.push(
          viewBy === "value" ? metric.orderValue : metric.orderCount,
        );
      });
    });

    return { labels, series };
  };

  const chartData = getChartData();
  const topCategoryLimit =
    selectedCategory === "status"
      ? chartData.labels.length
      : Math.max(Number(topCategoryCount) || 0, 0);
  const selectedBestSellers =
    bestSellerMetric === "quantity"
      ? tableMetrics?.bestSellersByQuantity
      : tableMetrics?.bestSellersByRevenue;

  const handlePieSliceClick = (event, chartContext, config) => {
    const dataPointIndex = config?.dataPointIndex;
    if (dataPointIndex == null || dataPointIndex < 0) return;

    const label = chartData.labels.slice(0, topCategoryLimit)[dataPointIndex];
    if (!label) return;

    if (selectedCategory === "status") {
      window.setTimeout(() => {
        navigate("/orders", { state: { selectedStatus: label } });
      }, 0);
      return;
    }

    if (selectedCategory === "state") {
      window.setTimeout(() => {
        navigate("/orders", { state: { selectedState: label } });
      }, 0);
      return;
    }

    if (selectedCategory === "buyerApp") {
      window.setTimeout(() => {
        navigate("/orders", { state: { selectedBapId: label } });
      }, 0);
    }
  };

  const renderTable = () => {
    const filteredLabels = chartData.labels.slice(0, topCategoryLimit);
    const filteredSeries = chartData.series.slice(0, topCategoryLimit);

    return (
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>{selectedCategory}</th>
            <th>{viewBy === "value" ? "Order Value" : "Order Count"}</th>
          </tr>
        </thead>
        <tbody>
          {filteredLabels.map((key, index) => (
            <tr key={key}>
              <td>{key}</td>
              <td>
                {formatWholeNumber(
                  filteredSeries[index]?.data.reduce((a, b) => a + b, 0) || 0,
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const getVisiblePieSeries = (rawSeries, minPercent = 0.015) => {
    const safeSeries = (rawSeries || []).map((v) => Number(v) || 0);
    const total = safeSeries.reduce((sum, v) => sum + v, 0);
    if (total <= 0) return safeSeries;

    const minVisibleValue = total * minPercent;
    const adjusted = [...safeSeries];
    let requiredBoost = 0;

    adjusted.forEach((value, index) => {
      if (value > 0 && value < minVisibleValue) {
        requiredBoost += minVisibleValue - value;
        adjusted[index] = minVisibleValue;
      }
    });

    if (requiredBoost <= 0) return adjusted;

    // Borrow from larger slices to keep total approximately stable.
    const donors = adjusted
      .map((value, index) => ({
        index,
        reducible: Math.max(value - minVisibleValue, 0),
      }))
      .filter((item) => item.reducible > 0);

    const totalReducible = donors.reduce((sum, item) => sum + item.reducible, 0);
    if (totalReducible <= 0) return adjusted;

    donors.forEach((item) => {
      const reduction = (requiredBoost * item.reducible) / totalReducible;
      adjusted[item.index] = Math.max(adjusted[item.index] - reduction, 0);
    });

    return adjusted;
  };

  const pieLabels = chartData.labels.slice(0, topCategoryLimit);
  const pieSeriesRaw = chartData.series
    .slice(0, topCategoryLimit)
    .map((s) => s.data.reduce((a, b) => a + b, 0));
  const pieSeriesVisible = getVisiblePieSeries(pieSeriesRaw);

  const customStartDate = dateRange?.[0] ? dayjs(dateRange[0]) : null;
  const customEndDate = dateRange?.[1] ? dayjs(dateRange[1]) : null;

  const disableFutureDates = (current) =>
    current && current.isAfter(dayjs().endOf("day"));

  const disableStartDate = (current) => {
    if (!current) return false;
    if (disableFutureDates(current)) return true;
    return customEndDate ? current.isAfter(customEndDate, "day") : false;
  };

  const disableEndDate = (current) => {
    if (!current) return false;
    if (disableFutureDates(current)) return true;
    return customStartDate ? current.isBefore(customStartDate, "day") : false;
  };

  const handleCustomStartDateChange = (date) => {
    onDateRangeChange([date || null, customEndDate || null]);
  };

  const handleCustomEndDateChange = (date) => {
    onDateRangeChange([customStartDate || null, date || null]);
  };

  return (
    <>
      {canViewOrderAnalytics ? (
        <>
          <div className="container-fluid">
            {/* --- Tabs Header --- */}
            <div className="row d-flex align-items-center ordertabs p-2">
              <div className="col d-flex p-0">
                <Tabs id="controlled-tab-example" activeKey={key} onSelect={(k) => setKey(k)}>
                  {(hasService(1) || hasService(2)) && (
                    <Tab
                      eventKey="orders"
                      title={
                        <Link to="/OrderDash" className="nav-link">
                          Orders
                        </Link>
                      }
                    >
                      <div></div>
                    </Tab>
                  )}

                  {hasService(6) && (
                    <Tab
                      eventKey="igm"
                      title={
                        <Link to="/IgmDash" className="nav-link">
                          IGM
                        </Link>
                      }
                    >
                      <div></div>
                    </Tab>
                  )}

                  {hasService(5) && (
                    <Tab
                      eventKey="rsf"
                      title={
                        <Link to="/RsfDash" className="nav-link">
                          RSF
                        </Link>
                      }
                    >
                      <div></div>
                    </Tab>
                  )}
                </Tabs>
              </div>
            </div>

            {/* --- Main Content --- */}
            {key === "orders" && (hasService(1) || hasService(2)) && 
            <div className="tableontent row">
              <div className="d-flex align-items-center ordertabs m-0 p-2">
                {/* Toggle Button */}
                <div className="col d-flex p-0">
                  <button
                    onClick={() =>
                      setViewBy(viewBy === "value" ? "count" : "value")
                    }
                    style={{
                      padding: "8px 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "#fff",
                      borderRadius: "20px",
                      width: "250px",
                      border: "none",
                      cursor: "pointer",
                      boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
                      fontSize: "14px",
                    }}
                  >
                    <span
                      style={{
                        color: viewBy === "value" ? "#575757" : "#CCC",
                        fontWeight: viewBy === "value" ? "600" : "normal",
                      }}
                    >
                      Order Value
                    </span>
                    {viewBy === "value" ? (
                      <FiToggleLeft size={30} style={{ color: "#575757" }} />
                    ) : (
                      <FiToggleRight size={30} style={{ color: "#575757" }} />
                    )}
                    <span
                      style={{
                        color: viewBy === "count" ? "#575757" : "#CCC",
                        fontWeight: viewBy === "count" ? "600" : "normal",
                      }}
                    >
                      Order Count
                    </span>
                  </button>
                </div>

                {/* Date Selection */}
                <div className="col d-flex justify-content-end align-items-center p-0">
                  <label className="formLabels mx-2 mt-1">Date Range :</label>

                  {selectedDateCategory === "custom" && (
                    <div
                      className="d-flex align-items-center"
                      style={{ gap: "8px", marginRight: 16 }}
                    >
                      <DatePicker
                        className="custom-range-picker"
                        format="MM/DD/YYYY"
                        allowClear
                        placeholder="Start Date"
                        value={customStartDate}
                        onChange={handleCustomStartDateChange}
                        disabledDate={disableStartDate}
                        style={{ width: 140 }}
                      />
                      <span style={{ color: "#6b7280", fontSize: "13px" }}>to</span>
                      <DatePicker
                        className="custom-range-picker"
                        format="MM/DD/YYYY"
                        allowClear
                        placeholder="End Date"
                        value={customEndDate}
                        onChange={handleCustomEndDateChange}
                        disabledDate={disableEndDate}
                        style={{ width: 140 }}
                      />
                    </div>
                  )}
                  <Select
                    style={{ width: 150, marginRight: 16 }}
                    onChange={handleSelectChange}
                    defaultValue="all"
                  >
                    <Option value="all">All</Option>
                    <Option value="last 5-years">Last 5 Years</Option>
                    <Option value="last 3-years">Last 3 Years</Option>
                    <Option value="last 1-year">Last Year</Option>
                    <Option value="last 6-months">Last 6 Months</Option>
                    <Option value="last 3-months">Last 3 Months</Option>
                    <Option value="last 1-month">Last Month</Option>
                    <Option value="last 1-week">Last Week</Option>
                    <Option value="today">Today</Option>
                    <Option value="custom">Custom</Option>
                  </Select>
                </div>
              </div>

              <div className="">
                <div className="row">
                  {/* line chart */}
                  <div className="col-6">
                    <div className="chart">
                      <div className="sales-order-card-container">
                        {/* Card 1: Lifetime Sales */}
                        <div
                          className="lifetime-sales-card"
                          style={{ padding: "1rem" }}
                        >
                          <FaHistory
                            size={32}
                            color="#fff"
                            className="float-right"
                          />
                          <div className="sales-image">
                            {/* <img src={myImage} alt="Background img" /> */}
                          </div>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="net-lifetime-sales-tooltip">
                               After Returns & Cancellations
                              </Tooltip>
                            }
                          >
                            <h4 style={{ display: "inline-block", cursor: "help" }}>
                              Net Lifetime Sales
                            </h4>
                          </OverlayTrigger>
                          <div
                            className="sales-value"
                            style={{
                              marginBottom: "6px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#fff",
                                flexShrink: 0,
                                minWidth: "75px",
                              }}
                            >
                              Order Value:
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {lifetimeSales?.ordervalue != null
                                ? `₹ ${formatWholeNumber(lifetimeSales.ordervalue)}`
                                : "0"}
                            </span>
                          </div>
                          <div
                            className="sales-count"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#fff",
                                flexShrink: 0,
                                minWidth: "75px",
                              }}
                            >
                              Order Count:
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {lifetimeSales?.ordercount != null
                                ? formatWholeNumber(lifetimeSales.ordercount)
                                : "0"}
                            </span>
                          </div>
                        </div>

                        {/* Card 2: Average Order */}
                        <div className="sales-order-card second">
                          <FaChartLine
                            size={32}
                            color="#fff"
                            className="float-right"
                          />
                          <div className="sales-image">
                            {/* <img src={myImage} alt="Background img" /> */}
                          </div>
                          <h4>Average Order</h4>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span>{tileMetrics?.averagevalue ? `₹` : ""}</span>
                            <span
                              style={{ fontSize: "24px", fontWeight: "600" }}
                            >
                              {tileMetrics?.averagevalue != null
                                ? `${formatWholeNumber(tileMetrics.averagevalue)}`
                                : "0"}
                            </span>
                          </div>
                        </div>
                        {/* Card 3: Total Sales/Count */}
                        <div className="sales-order-card first">
                          <FaShoppingCart
                            size={32}
                            color="#fff"
                            className="float-right"
                          />
                          <div className="sales-image">
                            {/* <img src={myImage} alt="Background img" /> */}
                          </div>
                          {viewBy === "value" ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="net-sales-tooltip">
                                  After Returns & Cancellations
                                </Tooltip>
                              }
                            >
                              <h4 style={{ display: "inline-block", cursor: "help" }}>
                                Net Sales
                              </h4>
                            </OverlayTrigger>
                          ) : (
                            <h4> Order Count</h4>
                          )}

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span>{viewBy === "value" ? `₹` : ``}</span>
                            <span
                              style={{ fontSize: "24px", fontWeight: "600" }}
                            >
                              {viewBy === "value"
                                ? formatWholeNumber(tileMetrics.ordervalue || 0)
                                : formatWholeNumber(tileMetrics.ordercount || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="sales-order-card-container">
                         {/* Card 6: Cancelled Orders */}
                        <div className="sales-order-card sixth">
                          <FaBan
                            size={32}
                            color="#fff"
                            className="float-right"
                          />
                          <div className="sales-image">
                            {/* <img src={myImage} alt="Background img" /> */}
                          </div>
                          <h4>Cancelled Orders</h4>
                           <div
                            className="sales-value"
                            style={{
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "0px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#fff",
                                flexShrink: 0,
                                minWidth: "75px",
                                marginRight: "6px",
                              }}
                            >
                              Cancelled Order Value:{" "}
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {tileMetrics?.cancelledordervalue != null
                                ? `₹ ${formatWholeNumber(tileMetrics.cancelledordervalue)}`
                                : "0"}
                            </span>
                          </div>
                          {/* <div
                            className="sales-count"
                            style={{
                              marginBottom: "6px",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#fff",
                                flexShrink: 0,
                                minWidth: "75px",
                              }}
                            >
                              Cancelled Average Value:{" "}
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {tileMetrics?.cancelledaveragevalue != null
                                ? " ₹ " + formatWholeNumber(tileMetrics.cancelledaveragevalue)
                                : "0"}
                            </span>
                          </div> */}
                                                    <div
                            className="sales-count"
                            style={{
                              display: "flex",
                              alignItems: "center",
                                                        gap: "6px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                color: "#fff",
                                flexShrink: 0,
                                minWidth: "75px",
                              }}
                            >
                              Cancelled Order Count:{" "}
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {tileMetrics?.cancelledordercount != null
                                ? formatWholeNumber(tileMetrics.cancelledordercount)
                                : "0"}
                            </span>
                          </div>
                        </div>
                       

                        {/* Card 5: Original Average Order */}
                        <div className="sales-order-card fifth">
                          <FaCalculator
                            size={32}
                            color="#fff"
                            className="float-right"
                          />
                          <div className="sales-image">
                            {/* <img src={myImage} alt="Background img" /> */}
                          </div>
                          <h4>Original Average </h4>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span>{tileMetrics?.originalaveragevalue ? `₹` : ""}</span>
                            <span
                              style={{ fontSize: "24px", fontWeight: "600" }}
                            >
                              {tileMetrics?.originalaveragevalue != null
                                ? `${formatWholeNumber(tileMetrics.originalaveragevalue)}`
                                : "0"}
                            </span>
                          </div>
                        </div>
                         {/* Card 4: Original Sales */}
                        <div className="sales-order-card fourth">
                          <FaMoneyBillAlt
                            size={32}
                            color="#fff"
                            className="float-right"
                          />
                          <div className="sales-image">
                            {/* <img src={myImage} alt="Background img" /> */}
                          </div>
                          {viewBy === "value" ? (
                              <h4 style={{ display: "inline-block", cursor: "help" }}>
                                Original Order Value
                              </h4>
                          ) : (
                            <h4> Original Order Count</h4>
                          )}

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span>{viewBy === "value" ? `₹` : ``}</span>
                            <span
                              style={{ fontSize: "24px", fontWeight: "600" }}
                            >
                              {viewBy === "value"
                                ? formatWholeNumber(tileMetrics.originalordervalue || 0)
                                : formatWholeNumber(Number(tileMetrics.ordercount) + Number(tileMetrics.cancelledordercount) || 0)}
                            </span>
                          </div>
                        </div>
                       
                      </div>
                      <div className="d-flex justify-content-end mb-2">
                        <button
                          onClick={handleRefresh}
                          className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                          style={{
                            padding: "4px 10px",
                            fontSize: "13px",
                          }}
                        >
                          <MdRefresh size={16} />
                          Refresh
                        </button>
                      </div>
                      
                      <Chart
                        key={`chart-${viewBy}`}
                        options={{
                          chart: {
                            toolbar: { show: false },
                            animations: {
                              enabled: true,
                            },
                          },
                          xaxis: {
                            categories: timeSeriesChartData.categories,
                            title: { text: "Period" },
                          },
                          yaxis: {
                            title: {
                              text:
                                viewBy === "value"
                                  ? "Total Order Value (₹)"
                                  : "Total Order Count",
                            },
                            labels: {
                              formatter: function (value) {
                                return Math.round(value);
                              },
                            },
                          },
                          stroke: { curve: "smooth" },
                          markers: {
                            enabled: true,
                            size: 5,
                            strokeWidth: 2,
                            strokeColors: ["#1E90FF"],
                            fillOpacity: 1,
                            hover: {
                              size: 7,
                            },
                          },
                          tooltip: {
                            theme: "light",
                            style: {
                              fontSize: "12px",
                              color: "#000000",
                            },
                            y: {
                              formatter: (val) =>
                                viewBy === "value"
                                  ? `₹${formatWholeNumber(val)}`
                                  : `${formatWholeNumber(val)}`,
                            },
                          },
                        }}
                        series={timeSeriesChartData.series}
                        type="line"
                        height={400}
                        width={"100%"}
                      />
                      <span
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          padding: "0 10px",
                          marginTop: "-20px",
                        }}
                      >
                        <b>
                          {viewBy === "value"
                            ? "Order Value Trend"
                            : "Order Count Trend"}
                        </b>
                      </span>
                    </div>
                 
                  </div>

                  {/* Pie Column */}
                  <div className="col-6">
                    <div className="chart">
                      <div className="mb-3">
                        <button
                          onClick={() => setSelectedCategory("status")}
                          style={{
                            padding: "4px 10px",
                            marginRight: "10px",
                            backgroundColor:
                              selectedCategory === "status" ? "#000" : "#CCC",
                            color: "white",
                            border: "none",
                            cursor: "pointer",
                            borderRadius: "8px",
                          }}
                        >
                          Status
                        </button>
                        <button
                          onClick={() => setSelectedCategory("state")}
                          style={{
                            padding: "4px 10px",
                            marginRight: "10px",
                            backgroundColor:
                              selectedCategory === "state" ? "#007BFF" : "#CCC",
                            color: "white",
                            border: "none",
                            cursor: "pointer",
                            borderRadius: "8px",
                          }}
                        >
                          State
                        </button>
                        <button
                          onClick={() => setSelectedCategory("buyerApp")}
                          style={{
                            padding: "4px 10px",
                            marginRight: "10px",
                            backgroundColor:
                              selectedCategory === "buyerApp"
                                ? "#007BFF"
                                : "#CCC",
                            color: "white",
                            border: "none",
                            cursor: "pointer",
                            borderRadius: "8px",
                          }}
                        >
                          NP
                        </button>
                        &nbsp;&nbsp;
                        <Select
                          defaultValue="pie"
                          style={{ width: 150 }}
                          onChange={(value) => setChartType(value)}
                        >
                          <Option value="pie">Pie Chart</Option>
                          <Option value="table">Table</Option>
                          <Option value="stacked">Stacked</Option>
                        </Select>
                        &nbsp;&nbsp;
                        {selectedCategory !== "status" && (
                          <Select
                            defaultValue="5"
                            style={{ width: 150 }}
                            onChange={(value) => setTopCategoryCount(value)}
                          >
                            <Option value="2">Top 2</Option>
                            <Option value="5">Top 5</Option>
                          </Select>
                        )}
                      </div>

                      {chartType === "pie" && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          {chartData.labels.length > 0 && topCategoryLimit > 0 ? (
                            <Chart
                              options={{
                                labels: pieLabels,
                                colors:
                                  selectedCategory === "status"
                                    ? pieLabels.map((label) => {
                                        switch (label) {
                                          case "Accepted":
                                            return "#a17ec9";
                                          case "Cancelled":
                                            return "#E57373";
                                          case "Completed":
                                            return "#66BB6A";
                                          case "In-progress":
                                            return "#FFEB3B";
                                          case "Created":
                                            return "#A2CFF7";
                                          default:
                                            return "#CCCCCC";
                                        }
                                      })
                                    : undefined,
                                dataLabels: { enabled: false },
                                tooltip: {
                                  theme: "light",
                                  style: {
                                    fontSize: "12px",
                                    color: "#000000",
                                  },
                                  y: {
                                    formatter: (_val, opts) => {
                                      const originalValue =
                                        pieSeriesRaw?.[opts?.seriesIndex] || 0;
                                      return viewBy === "value"
                                        ? `₹${formatWholeNumber(originalValue)}`
                                        : `${formatWholeNumber(originalValue)}`;
                                    },
                                  },
                                },
                                stroke: {
                                  width: 1,
                                  colors: ["#ffffff"],
                                },
                                legend: {
                                  position: "top",
                                  horizontalAlign: "left",
                                  offsetY: -10,
                                },
                                chart: {
                                  events: {
                                    dataPointSelection: handlePieSliceClick,
                                  },
                                },
                              }}
                              series={pieSeriesVisible}
                              type="pie"
                              height={500}
                            />
                          ) : (
                            <div className="no-data">
                              <p style={{ fontSize: "14px" }}>No data available</p>
                            </div>
                          )}
                        </div>
                      )}

                      {chartType === "stacked" && (
                        <Chart
                          options={{
                            chart: { type: "bar", stacked: true },
                            xaxis: {
                              categories: trendMetrics?.period || [],
                              title: { text: "Date" },
                            },
                            yaxis: {
                              title: {
                                text:
                                  viewBy === "value"
                                    ? "Order Value (₹)"
                                    : "Order Count",
                              },
                            },
                            plotOptions: { bar: { horizontal: false } },
                            legend: {
                              position: "top",
                              horizontalAlign: "center",
                            },
                            fill: { opacity: 1 },
                            dataLabels: { enabled: false },
                            tooltip: {
                              theme: "light",
                              style: {
                                fontSize: "12px",
                                color: "#000000",
                              },
                              y: {
                                formatter: (val) =>
                                  viewBy === "value"
                                    ? `₹${formatWholeNumber(val)}`
                                    : `${formatWholeNumber(val)}`,
                              },
                            },
                          }}
                          series={chartData.series.slice(0, topCategoryLimit)}
                          type="bar"
                          height={350}
                        />
                      )}

                      {chartType === "table" && renderTable()}
                      <span
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          padding: "0 10px",
                          marginTop: "-20px",
                        }}
                      >
                        <b>
                          {viewBy === "value" ? "Order Value" : "Order Count"}{" "}
                          by {selectedCategory}
                        </b>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bottom  Row */}
                <div className="row dashboard-containers">
                  {/* cards */}
                  <div className="col-6">
                    <div className="chart">
                      {/* Best Sellers List */}
                      <div className="col-12">
                        <div className="bestseller-header">
                          <h4 className="title bestseller-title">
                            Best Selling Products by {bestSellerMetric === "revenue" ? "Revenue" : "Quantity"}
                          </h4>
                          <div className="bestseller-filter">
                            <Select
                              size="small"
                              value={bestSellerMetric}
                              onChange={setBestSellerMetric}
                              style={{ width: 150 }}
                            >
                              <Option value="revenue">Revenue</Option>
                              <Option value="quantity">Quantity</Option>
                            </Select>
                          </div>
                        </div>
                        <div
                          className="card-container"
                          style={{
                            paddingBottom: "20px",
                            display: "flex",
                            gap: "16px",
                            flexWrap: "wrap",
                            alignItems: "stretch",
                            justifyContent: "left",
                          }}
                        >
                          {selectedBestSellers?.length > 0 ? (
                            selectedBestSellers.map((product, index) => (
                              <div
                                key={index}
                                className="product-card"
                                style={{
                                  flex: "0 1 calc(50% - 8px)",
                                  minWidth: "300px",
                                  position: "relative",
                                }}
                              >
                                <div className="product-info">
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "34px",
                                      right: "8px",
                                      zIndex: 10,
                                    }}
                                  >
                                    <CopyIcon
                                      textToCopy={
                                        product?.productname || "Test Product"
                                      }
                                    />
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip id={`product-name-${index}`}>
                                          {product?.productname ||
                                            "Test Product"}
                                        </Tooltip>
                                      }
                                    >
                                      <h6
                                        className="truncate"
                                        style={{
                                          fontSize: "14px",
                                          fontWeight: "500",
                                          marginBottom: "0",
                                          flex: 1,
                                        }}
                                      >
                                        {shortName(product)}
                                      </h6>
                                    </OverlayTrigger>
                                  </div>
                                  <div
                                    className="dflextag"
                                    style={{
                                      display: "flex",
                                      gap: "20px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <div
                                      className="product-details"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                      }}
                                    >
                                      <p
                                        className="ptag"
                                        style={{
                                          fontSize: "13px",
                                          marginBottom: "0",
                                          color: "#666",
                                        }}
                                      >
                                        Price:{" "}
                                      </p>
                                      <p
                                        className="pname"
                                        style={{
                                          fontSize: "13px",
                                          fontWeight: "600",
                                          marginBottom: "0",
                                        }}
                                      >
                                        ₹{product.productprice}
                                      </p>
                                    </div>
                                    <div
                                      className="product-details"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                      }}
                                    >
                                      <p
                                        className="ptag"
                                        style={{
                                          fontSize: "13px",
                                          marginBottom: "0",
                                          color: "#666",
                                        }}
                                      >
                                        {" "}
                                        Qty Sold:{" "}
                                      </p>
                                      <p
                                        className="pname"
                                        style={{
                                          fontSize: "13px",
                                          fontWeight: "600",
                                          marginBottom: "0",
                                        }}
                                      >
                                        {product.productqtysaled}
                                      </p>
                                    </div>
                                  </div>
                                  <div
                                    className="revenu product-details"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                      marginTop: "8px",
                                    }}
                                  >
                                    <p
                                      className="ptag"
                                      style={{
                                        fontSize: "13px",
                                        marginBottom: "0",
                                        color: "#666",
                                      }}
                                    >
                                      Revenue:
                                    </p>
                                    <p
                                      className="pname"
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        marginBottom: "0",
                                      }}
                                    >
                                      ₹{product.revenuegenerated}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="no-data">
                              <p style={{ fontSize: "14px" }}>
                                No data available
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* map*/}
                  <div className="col-6">
                    <div className="chart">
                      <h4 style={{ textAlign: "center" }} className="title">
                        Regional Order Distribution
                      </h4>
                      {mapLegends.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            flexWrap: "wrap",
                            gap: "20px",
                            marginBottom: "15px",
                            padding: "10px",
                          }}
                        >
                          {mapLegends.map((legend) => (
                            <div
                              key={legend.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <div
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "2px",
                                  backgroundColor: legend.color,
                                }}
                              />
                              <span
                                style={{ fontSize: "13px", fontWeight: "500" }}
                              >
                                {viewBy === "value"
                                  ? `${legend.name}: ₹${legend.value.toLocaleString("en-IN")}`
                                  : `${legend.name}: ${legend.count.toLocaleString("en-IN")}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div
                        id="chartdiv"
                        style={{ width: "100%", height: "80%" }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            }
            
            {/* IGM Dashboard Content */}
            {key === "igm" && hasService(6) && (
              <IgmAnalyticsDashboard />
            )}
            
            {/* RSF Dashboard Content */}
            {key === "rsf" && hasService(5) && (
              <RsfAnalyticsDashboard />
            )}
          </div>
        </>
      ) : (
        // Replace with your UnAuthorized component
        <div className="p-5 text-center">
          You are not authorized to view this page.
        </div>
      )}
    </>
  );
};

export default Analytics;
